import {useCallback, useSyncExternalStore} from 'react'

import {logger} from '#/logger'

// HTTP status codes that indicate recoverable (transient) failures
const RECOVERABLE_STATUSES = new Set([
  408, 425, 429, 500, 502, 503, 504, 522, 524,
])

export type PendingMessageStatus = 'sending' | 'sent' | 'failed'

export type MatrixPendingMessage = {
  id: string
  roomId: string
  /** Display body (filename for attachments, text for messages) */
  body: string
  /** Timestamp for ordering */
  timestamp: number
  status: PendingMessageStatus
  /** True if the failure is transient (network) and can be auto-retried */
  recoverable: boolean
  /** Retry callback — present when status is 'failed' */
  retry?: () => void
  /** Error description */
  error?: string
  /** Any extra data the caller wants to stash (e.g. localUri, attachment info) */
  meta?: Record<string, unknown>
}

type Listener = () => void

/**
 * In-memory store for pending Matrix messages across all rooms.
 *
 * Mirrors the ATProto Convo pattern: messages are tracked with a status
 * (sending/sent/failed), failures are classified as recoverable vs
 * unrecoverable, and recoverable failures are auto-retried on reconnect.
 */
class PendingMessageStore {
  private messages = new Map<string, MatrixPendingMessage>()
  private listeners = new Set<Listener>()
  private version = 0

  /** Subscribe to store changes (for useSyncExternalStore) */
  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  /** Snapshot for useSyncExternalStore — returns version to trigger re-renders */
  getSnapshot = (): number => this.version

  private notify() {
    this.version++
    for (const listener of this.listeners) {
      listener()
    }
  }

  /** Add a new pending message */
  add(msg: Omit<MatrixPendingMessage, 'status' | 'recoverable'>) {
    this.messages.set(msg.id, {
      ...msg,
      status: 'sending',
      recoverable: false,
    })
    this.notify()
  }

  /** Mark a message as sent (will be cleaned up shortly) */
  markSent(id: string) {
    const msg = this.messages.get(id)
    if (!msg) return
    this.messages.set(id, {...msg, status: 'sent', error: undefined})
    this.notify()
  }

  /** Mark a message as failed with error classification */
  markFailed(
    id: string,
    error: string,
    opts?: {httpStatus?: number; retry?: () => void},
  ) {
    const msg = this.messages.get(id)
    if (!msg) return

    const recoverable = opts?.httpStatus
      ? RECOVERABLE_STATUSES.has(opts.httpStatus)
      : false

    this.messages.set(id, {
      ...msg,
      status: 'failed',
      error,
      recoverable,
      retry: opts?.retry,
    })
    this.notify()
  }

  /** Remove a message from the store */
  remove(id: string) {
    if (this.messages.delete(id)) {
      this.notify()
    }
  }

  /** Remove all sent messages for a room */
  clearSent(roomId: string) {
    let changed = false
    for (const [id, msg] of this.messages) {
      if (msg.roomId === roomId && msg.status === 'sent') {
        this.messages.delete(id)
        changed = true
      }
    }
    if (changed) this.notify()
  }

  /** Get all pending messages for a room, ordered by timestamp */
  getForRoom(roomId: string): MatrixPendingMessage[] {
    const result: MatrixPendingMessage[] = []
    for (const msg of this.messages.values()) {
      if (msg.roomId === roomId) {
        result.push(msg)
      }
    }
    return result.sort((a, b) => a.timestamp - b.timestamp)
  }

  /**
   * Retry all recoverable failures for a room (called on reconnect).
   * Each message's retry() callback is invoked.
   */
  retryRecoverable(roomId?: string) {
    for (const msg of this.messages.values()) {
      if (roomId && msg.roomId !== roomId) continue
      if (msg.status === 'failed' && msg.recoverable && msg.retry) {
        logger.info('Auto-retrying pending message', {id: msg.id})
        this.messages.set(msg.id, {...msg, status: 'sending'})
        msg.retry()
      }
    }
    this.notify()
  }

  /** Check if there are any failed messages for a room */
  hasFailedMessages(roomId: string): boolean {
    for (const msg of this.messages.values()) {
      if (msg.roomId === roomId && msg.status === 'failed') return true
    }
    return false
  }
}

/** Singleton store instance */
export const pendingMessageStore = new PendingMessageStore()

/**
 * React hook for accessing pending messages for a specific room.
 * Re-renders when the store changes.
 */
export function usePendingMessages(roomId: string) {
  const version = useSyncExternalStore(
    pendingMessageStore.subscribe,
    pendingMessageStore.getSnapshot,
  )

  // Re-derive on every version change
  const messages = pendingMessageStore.getForRoom(roomId)
  const hasFailedMessages = pendingMessageStore.hasFailedMessages(roomId)

  const retryAll = useCallback(() => {
    pendingMessageStore.retryRecoverable(roomId)
  }, [roomId])

  return {
    pendingMessages: messages,
    hasFailedMessages,
    retryAll,
    // Expose version to ensure the hook is seen as depending on the store
    _version: version,
  }
}
